import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ScanQrOverlay } from '@/components/qr/ScanQrOverlay';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';

interface NavItem {
  to: string;
  label: string;
  icon: (active: boolean) => JSX.Element;
}

/**
 * Bottom nav do Bow Redesign — barra cream-blur com tinta navy.
 * Item ativo: navy 100% + dot emerald acima do ícone.
 * Item inativo: navy 45%.
 *
 * 4 destinos + botão CENTRAL de scan (padrão Tupi/Spott/Move — o usuário de
 * EV está acostumado a escanear o QR de dentro do app): Início, Saldo,
 * [Escanear], Aliança, Você. Histórico em "Ver tudo" no Saldo.
 */
const items: NavItem[] = [
  {
    to: '/',
    label: 'Início',
    icon: (active) => (
      <svg
        viewBox="0 0 24 24"
        className="h-[22px] w-[22px]"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.2 : 1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M3 11.5 12 4l9 7.5" />
        <path d="M5 10v10h14V10" />
      </svg>
    )
  },
  {
    to: '/wallet',
    label: 'Saldo',
    icon: (active) => (
      <svg
        viewBox="0 0 24 24"
        className="h-[22px] w-[22px]"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.2 : 1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="6" width="18" height="14" rx="3" />
        <path d="M16 13h2" />
        <path d="M3 10h18" />
      </svg>
    )
  },
  {
    to: '/alianca',
    label: 'Aliança',
    icon: (active) => (
      <img
        src="/logo/logo-bow-13.png"
        alt="Aliança"
        style={{ height: active ? 24 : 22, width: active ? 24 : 22 }}
        className={active ? 'opacity-100' : 'opacity-50'}
      />
    )
  },
  {
    to: '/profile',
    label: 'Você',
    icon: (active) => (
      <svg
        viewBox="0 0 24 24"
        className="h-[22px] w-[22px]"
        fill="none"
        stroke="currentColor"
        strokeWidth={active ? 2.2 : 1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
      </svg>
    )
  }
];

export function BottomNav() {
  const navigate = useNavigate();
  const [scanOpen, setScanOpen] = useState(false);
  // Teclado aberto esconde a barra (padrão de app nativo). No Android o
  // adjustResize encolhe o layout viewport e esta nav fixed sobe pra CIMA do
  // teclado — exatamente onde as telas NSS ancoram o CTA com kbHeight, que usa
  // o visualViewport inteiro sem descontar a nav. Era o "botão de pagar some"
  // do Adicionar saldo (docs/bugs/2026-08-15-cta-teclado-bottomnav-android.md).
  // No iOS o layout viewport não encolhe (nav já ficava atrás do teclado) —
  // esconder é inócuo. resetScroll=false: estas rotas rolam o documento.
  const { kbOpen } = useKeyboardViewport(120, false);

  const renderItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `relative flex flex-col items-center justify-center gap-0.5 rounded-2xl py-1.5 text-[11px] font-medium transition ${
          isActive ? 'text-bow-royal' : 'text-bow-royal/45'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden
            className={`absolute top-0 h-1 w-1 rounded-full bg-bow-green transition-opacity ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
          />
          {item.icon(isActive)}
          <span className={isActive ? 'font-semibold' : ''}>{item.label}</span>
        </>
      )}
    </NavLink>
  );

  return (
    <>
      <nav
        className={`fixed inset-x-0 bottom-0 z-10 grid grid-cols-5 border-t border-bow-royal/10 bg-bow-cream/85 px-2 pt-2 backdrop-blur-xl ${
          kbOpen ? 'hidden' : ''
        }`}
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        {items.slice(0, 2).map(renderItem)}

        {/* Botão central de scan — círculo navy elevado (padrão dos apps EV) */}
        <div className="relative flex flex-col items-center justify-end pb-1">
          <button
            type="button"
            onClick={() => setScanOpen(true)}
            aria-label="Escanear QR do carregador"
            className="absolute -top-7 flex h-14 w-14 items-center justify-center rounded-full bg-bow-royal text-bow-cream shadow-deep transition active:scale-95"
          >
            <QrScanIcon />
          </button>
          <span className="text-[11px] font-medium text-bow-royal/45">
            Escanear
          </span>
        </div>

        {items.slice(2).map(renderItem)}
      </nav>

      {scanOpen && (
        <ScanQrOverlay
          onClose={() => setScanOpen(false)}
          onResolved={(path) => {
            setScanOpen(false);
            navigate(path);
          }}
        />
      )}
    </>
  );
}

function QrScanIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="26"
      height="26"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {/* Cantos de mira */}
      <path d="M3 8V5a2 2 0 0 1 2-2h3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M21 16v3a2 2 0 0 1-2 2h-3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      {/* Miolo QR estilizado */}
      <rect x="8" y="8" width="3.2" height="3.2" rx="0.6" />
      <rect x="13" y="8" width="3.2" height="3.2" rx="0.6" />
      <rect x="8" y="13" width="3.2" height="3.2" rx="0.6" />
      <path d="M13 14.6h3.2M14.6 13v3.2" />
    </svg>
  );
}
